import { MenuItem, Stack, Typography } from "@mui/material";
// import { fromZonedTime } from "date-fns-tz";
import { addHours } from "date-fns/addHours";
import { useEffect, useMemo, type JSX } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { SetpointScheduleUnitEnum } from "../../common/enums/SetpointScheduleUnitEnum";
import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";
import { IeLynxObject } from "../../common/models/eLynxObject";
import { useConfirmation } from "../../contexts/confirmation-context/ConfirmationContext";
import { BindDialogReturn, DialogProps, DialogState, bindDialog, useDialogState } from "../../hooks/use-dialog-state/UseDialogState";
import { useRouteContext } from "../../hooks/use-route-context/UseRouteContext";
import { useQueryNotifications } from "../../queries/UseQueryNotifications";
import { useDisableObjectSetpoints } from "../../queries/alarm-setpoints/UseDisableObjectSetpoints";
import { useDisableTagSetpoint } from "../../queries/alarm-setpoints/UseDisableTagSetpoint";
import { useEnableTagSetpoint } from "../../queries/alarm-setpoints/UseEnableTagSetpoint";
import { useObjectSetpointDisabledSchedule } from "../../queries/alarm-setpoints/UseObjectSetpointDisabledSchedule";
import { useTagSetpointDisabledSchedule } from "../../queries/alarm-setpoints/UseTagSetpointDisabledSchedule";
import { useTimezones } from "../../queries/time-zones/UseTimezones";
import { defaultTimezoneValue } from "../../services/global-data-service/GlobalDataService";
import FormCheckbox from "../forms/form-checkbox/FormCheckbox";
import FormDateRange from "../forms/form-date-range/FormDateRange";
import FormDateTimePicker from "../forms/form-date-time-picker/FormDateTimePicker";
import FormInput from "../forms/form-input/FormInput";
import FormSelect from "../forms/form-select/FormSelect";
import Form from "../forms/form/Form";
import ResponsiveDialog from "../responsive-dialog/ResponsiveDialog";
import UnsavedChangesPrompt from "../unsaved-changes-prompt/UnsavedChangesPrompt";
import { tz } from "@date-fns/tz";
import { transpose } from "date-fns/transpose";

export type AlarmSetpointDisableDialogInitValues = {
    // Tag/Setpoints
    tagID?: number;
    setpointID?: number;

    // All Object Setpoints
    targetObject?: IeLynxObject;
};

export type AlarmSetpointDisableDialogProps = DialogProps<AlarmSetpointDisableDialogInitValues>;

enum ViewMode {
    disableFor = 0,
    disableBetween = 1
}

type AlarmSetpointDisableDialogFormValuesType = {
    disable: boolean;
    viewMode: ViewMode;
    duration: number;
    durationUnit: SetpointScheduleUnitEnum;
    timeframe: {
        startDate: Date;
        endDate: Date;
    },
    timezoneIndex: number;
};

function getDefaultFormValues(disable: boolean): AlarmSetpointDisableDialogFormValuesType {
    const startDate = new Date();
    const endDate = addHours(startDate, 1);
    return {
        disable: disable,
        viewMode: ViewMode.disableFor,

        duration: 1,
        durationUnit: SetpointScheduleUnitEnum.Hour,
        timeframe: {
            startDate: startDate,
            endDate: endDate
        },
        timezoneIndex: defaultTimezoneValue.id
    };
}

export default function AlarmSetpointDisableDialog({
    open,
    onClose,
    values
}: AlarmSetpointDisableDialogProps): JSX.Element {

    const routeContext = useRouteContext();
    const { t } = useTranslation();
    const { confirm, acknowledge } = useConfirmation();
    const allSetpoints = !!values?.targetObject;

    const form = useForm<AlarmSetpointDisableDialogFormValuesType>({
        mode: "onChange",
        defaultValues: getDefaultFormValues(false)
    });

    const {
        handleSubmit,
        formState: {
            isDirty,
            isValid
        },
        reset,
        setValue,
        watch,
        trigger
    } = form;

    const { data: timezones } = useQueryNotifications(useTimezones)();

    ////////////////////////////////////////////////////////////////////////////////////
    const { data: tagDisabledSchedule } = useQueryNotifications(useTagSetpointDisabledSchedule)({
        groupID: routeContext.groupID as number,
        tagID: values?.tagID as number,
        setpointID: values?.setpointID as number,
        enabled: (values?.tagID ?? 0) > 0 && (values?.setpointID ?? 0) > 0
    });

    const { mutateAsync: enableTagSetpoint } = useQueryNotifications(useEnableTagSetpoint)({
        groupID: routeContext.groupID as number,
        tagID: values?.tagID as number,
        setpointID: values?.setpointID as number,
        onSuccess: () => {
            acknowledge(t("alarmSetpointList.enabledNotificationText"));
        }
    });

    const { mutateAsync: disableTagSetpoint } = useQueryNotifications(useDisableTagSetpoint)({
        groupID: routeContext.groupID as number,
        tagID: values?.tagID as number,
        setpointID: values?.setpointID as number,
        onSuccess: () => {
            acknowledge(t("alarmSetpointList.disabledNotificationText"));
        }
    });
    ////////////////////////////////////////////////////////////////////////////////////

    ////////////////////////////////////////////////////////////////////////////////////
    const { data: objectDisabledSchedule } = useQueryNotifications(useObjectSetpointDisabledSchedule)({
        groupID: routeContext.groupID as number,
        objectType: values?.targetObject?.objectType as eLynxObjectTypeEnum,
        objectID: values?.targetObject?.id as number,
        enabled: !!values?.targetObject
    });

    const { mutateAsync: disableObjectSetPoints } = useQueryNotifications(useDisableObjectSetpoints)({
        groupID: routeContext.groupID as number,
        objectType: values?.targetObject?.objectType as eLynxObjectTypeEnum,
        objectID: values?.targetObject?.id as number,
        onSuccess: () => {
            acknowledge(t("alarmSetpointList.disableAllAlarmsAcknowledge"));
        }
    });
    ////////////////////////////////////////////////////////////////////////////////////

    const disabledSchedule = useMemo(() => {
        return allSetpoints
            ? objectDisabledSchedule
            : tagDisabledSchedule;
    }, [
        allSetpoints,
        objectDisabledSchedule,
        tagDisabledSchedule
    ]);

    useEffect(() => {

        if (disabledSchedule /*&& open*/) {
            const timezone = timezones?.find(tz => tz?.standardName === disabledSchedule?.timezone);

            reset({
                disable: true,
                viewMode: disabledSchedule?.defaultDuration === false
                    ? ViewMode.disableBetween
                    : ViewMode.disableFor,

                duration: disabledSchedule?.duration ?? 1,
                durationUnit: disabledSchedule?.durationUnit ?? SetpointScheduleUnitEnum.Hour,
                timeframe: {
                    startDate: disabledSchedule?.beginsOn
                        ? new Date(disabledSchedule.beginsOn)
                        : new Date(),

                    endDate: disabledSchedule?.endsOn
                        ? new Date(disabledSchedule.endsOn)
                        : new Date()
                },
                timezoneIndex: timezone?.id ?? defaultTimezoneValue.id
            });
        } else {
            reset(
                getDefaultFormValues(allSetpoints)
            );
        }

    }, [
        disabledSchedule,
        allSetpoints,
        //open,
        reset,
        timezones
    ]);

    async function onOkay(data: AlarmSetpointDisableDialogFormValuesType) {
        await save(data);

        onClose({
            reason: "ok"
        });
    }

    async function onCancel() {
        const canCancel = isDirty
            ? await getUserConfirmation()
            : true;

        if (canCancel) {
            onClose({
                reason: "cancel"
            });
        }
    }

    async function getUserConfirmation() {

        return (await confirm({
            dialogTitle: t("cancelConfirmation.title"),
            confirmationText: t("disableAlarmSetpoint.cancelText"),
            yesButtonText: t("cancelConfirmation.yesButtonText"),
            noButtonText: t("cancelConfirmation.noButtonText"),
            showConfirmationText: true,
            showYesButton: true,
            showNoButton: true
        })) === "yes";
    }

    async function save(data: AlarmSetpointDisableDialogFormValuesType) {
        const timezone = timezones?.find(timezone => timezone.id === data.timezoneIndex);

        // const beginsOn = fromZonedTime(data.timeframe.startDate, timezone?.olsonTimezone as string);
        // const endsOn = fromZonedTime(data.timeframe.endDate, timezone?.olsonTimezone as string);
        const beginsOn = transpose(data.timeframe.startDate, tz(timezone?.olsonTimezone as string));
        const endsOn = transpose(data.timeframe.endDate, tz(timezone?.olsonTimezone as string));

        if (allSetpoints) {
            await disableObjectSetPoints({
                tagID: -1,//values?.tagID as number,
                setPointID: -1,//values?.setpointID as number,
                duration: data.duration,
                durationUnit: data.durationUnit,
                beginsOn: beginsOn,
                endsOn: endsOn,
                timezone: timezone?.standardName ?? defaultTimezoneValue.standardName,
                defaultDuration: data.viewMode === ViewMode.disableFor
            });
        } else {
            if (data.disable) {
                await disableTagSetpoint({
                    tagID: values?.tagID as number,
                    setPointID: values?.setpointID as number,
                    duration: data.duration,
                    durationUnit: data.durationUnit,
                    beginsOn: beginsOn,
                    endsOn: endsOn,
                    timezone: timezone?.standardName ?? defaultTimezoneValue.standardName,
                    defaultDuration: data.viewMode === ViewMode.disableFor
                });
            } else {
                await enableTagSetpoint();
            }
        }
    }

    const [
        disable,
        viewMode,
        startDate,
        endDate
    ] = watch([
        "disable",
        "viewMode",
        'timeframe.startDate',
        'timeframe.endDate'
    ]);


    useEffect(() => {


        if (endDate.valueOf() < startDate.valueOf()) {

            const d2 = addHours(startDate, 1);

            setValue('timeframe', {
                startDate,
                endDate: d2
            });

            trigger();
        }

    }, [
        startDate,
        endDate,
        trigger,
        setValue
    ]);

    return (
        <>
            <ResponsiveDialog
                title={
                    allSetpoints
                        ? t("disableAlarmSetpoint.titleAll")
                        : t("disableAlarmSetpoint.title")
                }
                enableOkay={(isDirty && isValid) || allSetpoints}
                okayLabel={t("buttons.save")}
                open={open}
                onClose={
                    (reason: string) => {
                        if (reason === "ok") {
                            handleSubmit(async (data) => onOkay(data))();
                        } else {
                            onCancel();
                        }
                    }
                }
            >
                <Typography component="span" variant="body1">
                    {allSetpoints
                        ? t("disableAlarmSetpoint.helpTextAll")
                        : t("disableAlarmSetpoint.helpText")}
                </Typography>

                <Form form={form}>
                    <Stack spacing={2}>
                        {!allSetpoints &&
                            <FormCheckbox
                                name="disable"
                                label={t("disableAlarmSetpoint.disableText")}
                            />
                        }
                        <FormSelect
                            name="viewMode"
                            label={t("disableAlarmSetpoint.selectMode")}
                            rules={{
                                required: false
                            }}
                            disabled={!disable}
                        >
                            <MenuItem key={0} value={0}>Disable For</MenuItem>
                            <MenuItem key={1} value={1}>Disable Between</MenuItem>
                        </FormSelect>

                        <Stack
                            direction="row"
                            spacing={2}
                            sx={{
                                alignItems: "center",
                                display: viewMode === 0 ? "inherit" : "none"
                            }}>
                            <FormInput
                                name="duration"
                                label={t("disableAlarmSetpoint.duration")}
                                sx={{
                                    flexGrow: 1
                                }}
                                disabled={!disable}
                                //onChange={() => onDurationChanged()}
                            />
                            <FormSelect
                                name="durationUnit"
                                label={t("disableAlarmSetpoint.durationUnit")}
                                sx={{
                                    flexGrow: 4
                                }}
                                rules={{
                                    required: false
                                }}
                                disabled={!disable}
                                //onChange={() => onDurationChanged()}
                            >
                                <MenuItem key={SetpointScheduleUnitEnum.Hour} value={SetpointScheduleUnitEnum.Hour}>Hour</MenuItem>
                                <MenuItem key={SetpointScheduleUnitEnum.Day} value={SetpointScheduleUnitEnum.Day}>Day</MenuItem>
                                <MenuItem key={SetpointScheduleUnitEnum.Week} value={SetpointScheduleUnitEnum.Week}>Week</MenuItem>
                                <MenuItem key={SetpointScheduleUnitEnum.Month} value={SetpointScheduleUnitEnum.Month}>Month</MenuItem>
                                <MenuItem key={SetpointScheduleUnitEnum.Year} value={SetpointScheduleUnitEnum.Year}>Year</MenuItem>
                            </FormSelect>
                        </Stack>

                        <FormDateTimePicker
                            name="timeframe.startDate"
                            label={t("disableAlarmSetpoint.beginning")}
                            sx={{
                                flexGrow: 1,
                                display: viewMode === 0 ? "inherit" : "none"
                            }}
                            disabled={!disable}
                        />

                        <FormDateRange
                            name="timeframe"
                            sx={{ marginLeft: -2, display: viewMode === 1 ? "inherit" : "none" }}
                            disabled={!disable}
                        />

                        <FormSelect
                            name="timezoneIndex"
                            label={t("disableAlarmSetpoint.timezone")}
                            rules={{
                                required: false
                            }}
                            disabled={!disable}
                        >
                            {timezones?.map((o, index) =>
                                <MenuItem key={`tz-${index}`} value={o.id}>
                                    {o.name}
                                </MenuItem>)}
                        </FormSelect>
                    </Stack>
                </Form>
            </ResponsiveDialog>
            <UnsavedChangesPrompt
                hasChanges={isDirty}
                getUserConfirmation={getUserConfirmation}
            />
        </>
    );
}

export type AlarmSetpointDisableDialogState = DialogState<AlarmSetpointDisableDialogInitValues>;
export type BindAlarmSetpointDisableDialogReturn = BindDialogReturn<AlarmSetpointDisableDialogInitValues>;

export function useAlarmSetpointDisableDialogState(): AlarmSetpointDisableDialogState {
    return useDialogState();
}

export function bindAlarmSetpointDisableDialog(props: AlarmSetpointDisableDialogState): BindAlarmSetpointDisableDialogReturn {
    return bindDialog(props);
}
